<template>
  <el-row v-loading="loading" class="pond-abw-tab">
    <el-col>
      <er-card>
        <er-data-tables
          :tableData="tableData"
          :columns="tableColumns"
          :action-column="false"
          uniqueId="pond-abw-tab"
          type="white-header-table"
          size="mini"
          :columnSelector="true"
          :key="$i18n.locale"
        >
          <template v-slot:doc="{ row }">{{
            row.data.day_of_culture
          }}</template>
          <template v-slot:biomass="{ row }">{{
            calculateBiomass(row.data)
              | numberWithNDigitPrecision(0)
              | commas(getUserCountryCode)
              | na
          }}</template>
          <template v-slot:adg="{ row }">{{
            row.data.adg | numberWithNDigitPrecision(2) | na
          }}</template>
          <template v-slot:event_date="{ row }">
            <span style="color:#155DBE">{{
              $moment.utc(row.data.date).format("DD MMM, YY")
            }}</span>
          </template>

          <template v-slot:actions="{ row }">
            <el-row type="flex" align="middle">
              <er-button
                type="text"
                size="mini"
                btnType="edit"
                :showLabel="true"
                :showIcon="true"
                :disabled="disabledBtn"
                @click="handleEditABWRecord(row.data)"
              ></er-button>
              <el-divider
                direction="vertical"
                style="height:100%;"
              ></el-divider>
              <er-button
                type="text"
                size="mini"
                btnType="delete"
                :showLabel="true"
                :showIcon="true"
                :disabled="disabledBtn"
                @click="handleDeleteABWRecord(row.data)"
              ></er-button>
            </el-row>
          </template>
        </er-data-tables>
      </er-card>
    </el-col>

    <er-dialog
      :visible.sync="showDialog"
      width="30%"
      :close-on-click-modal="false"
      @close="handleDialogClose"
      :title="`${$t('Comn_update')} ${$t('ABW_abw')}`"
      custom-class="pondABWTab_Dialog"
    >
      <el-form
        size="small"
        :ref="formName"
        :rules="rules"
        :model="editABWObj"
        :hide-required-asterisk="true"
      >
        <el-form-item
          :label="$t('Comn_pond_name')"
          prop="pond_id"
          :required="true"
        >
          <el-col :span="20">
            <er-select
              filterable
              size="small"
              v-model="editABWObj.pond_id"
              :disabled="true"
              value-key="_id"
            >
              <el-option
                v-for="(pond, pond_id) in getPondIdPondMap"
                :key="pond_id"
                :value="pond._id"
                :label="pond.title"
              ></el-option>
            </er-select>
          </el-col>
        </el-form-item>
        <el-form-item :label="$t('ABW_abw')" prop="abw">
          <el-col :span="20">
            <el-input-number
              size="small"
              :controls="false"
              :min="0"
              :precision="2"
              v-model="editABWObj.abw"
              @change="handleChangeInABW($event, editABWObj)"
            ></el-input-number>
          </el-col>
        </el-form-item>
        <el-form-item :label="$t('Comn_awg')" prop="awg">
          <el-col :span="20">
            <el-input-number
              size="small"
              :controls="false"
              :precision="2"
              v-model="editABWObj.awg"
              disabled
            ></el-input-number>
          </el-col>
        </el-form-item>
        <el-form-item
          :label="$t('Comn_survival_percentage')"
          prop="survival_percentage"
        >
          <el-col :span="20">
            <el-input-number
              v-model="editABWObj.survival_percentage"
              size="small"
              :controls="false"
              :max="100"
              :min="0"
            ></el-input-number>
          </el-col>
        </el-form-item>
        <el-form-item :label="$t('Comn_date_and_time')">
          <el-col :span="20">
            <el-input
              :value="
                $moment(editABWObj.date || 1)
                  .utc()
                  .format('DD MMM, YY')
              "
              disabled
            >
            </el-input>
          </el-col>
        </el-form-item>
        <el-form-item :label="$t('ABW_samples_total_weight')">
          <el-col :span="20">
            <el-input
              size="small"
              disabled
              v-model="editABWObj.sample_total_weight"
            >
              <template slot="append">
                <samples-table2
                  :popover-data="editABWObj"
                  @cancelled="handleSampleTableCancel"
                >
                  <template slot="reference">
                    <er-button size="mini" type="primary" :label="true">
                      {{ $t("Comn_edit") }}
                    </er-button>
                  </template>
                </samples-table2>
              </template>
            </el-input>
          </el-col>
        </el-form-item>
      </el-form>
      <layout-toolbar slot="footer" justify="center" class="dialog-footer">
        <er-button
          size="mini"
          btnType="save"
          :showLabel="true"
          :showIcon="true"
          :loading="loading"
          @click="validateFormType2(handleSaveEdits)"
          :disabled="isReadOnly"
        ></er-button>
        <er-button
          size="mini"
          btnType="cancel"
          :showLabel="true"
          @click="handleCancelClick"
          :disabled="isReadOnly"
        ></er-button>
      </layout-toolbar>
    </er-dialog>
  </el-row>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import formRulesMixin from "@/mixins/formRulesMixin";
import errorHandlerMixin from "@/mixins/errorHandlerMixin";
import filtersMixin from "@/mixins/filtersMixin";
import samplesTable2 from "./samplesTable2";
import { Samples } from "@/model/abw";
import notificationMixin from "@/mixins/notificationMixin";

export default {
  props: ["tabData"],
  mixins: [formRulesMixin, errorHandlerMixin, filtersMixin, notificationMixin],
  components: {
    samplesTable2
  },
  data: function() {
    return {
      dateRangeSelected: [],
      showDialog: false,
      editABWObj: {
        pond_id: "",
        date: "",
        abw: 0,
        awg: 0,
        sample_total_weight: 0,
        survival_percent: 100,
        samples: [],
        searchPond: ""
      },
      errMessagesObj: {
        pond_id: "",
        date: "",
        abw: 0,
        awg: 0,
        sample_total_weight: 0,
        survival_percent: 100,
        samples: []
      },
      formName: "editAbwForm",
      tableData: [],
      loading: false,
      cancelObjCopy: {}
    };
  },
  computed: {
    ...mapGetters("pondDetails", {
      getPondIdPondMap: "getPondIdPondMap",
      getSelectedPondId: "getSelectedPondId",
      getArrSelectedPondABWRecords: "getArrSelectedPondABWRecords",
      getCurrentCulture: "getCurrentCulture",
      getUserTimeZoneOffset: "getUserTimeZoneOffset",
      getSelectedPondName: "getSelectedPondName"
    }),
    ...mapGetters("user", {
      isReadOnly: "isReadOnly",
      getUserCountryCode: "getUserCountryCode"
    }),
    disabledBtn() {
      return this.isReadOnly || !this.getCurrentCulture.is_running;
    },
    rules: function() {
      const abwTrans = this.$t("ABW_abw");
      const survivalPercentTrans = this.$t("Comn_survival_percentage");
      return {
        abw: [
          {
            type: "number",
            required: true,
            message: this.$t("Comn_must_not_be_empty", {
              field: abwTrans
            }),
            trigger: "blur"
          },
          {
            type: "number",
            min: 0,
            message: this.$tc("Comn_must_greater_than", 2, {
              field: abwTrans,
              threshold: 0
            }),
            trigger: "blur"
          }
        ],
        survival_percentage: [
          {
            type: "number",
            required: true,
            message: this.$t("Comn_must_not_be_empty", {
              field: survivalPercentTrans
            }),
            trigger: "blur"
          },
          {
            type: "number",
            min: 0,
            message: this.$tc("Comn_must_greater_than", 2, {
              field: survivalPercentTrans,
              threshold: 0
            }),
            trigger: "blur"
          }
        ]
      };
    },
    tableColumns: function() {
      return {
        doc: {
          prop: "day_of_culture",
          label: this.$t("Comn_doc"),
          minWidth: 80,
          required: true
        },
        abw: {
          prop: "abw",
          label: this.$t("abw_gms"),
          minWidth: 100,
          required: true
        },
        awg: {
          prop: "awg",
          label: this.$t("Comn_awg_g"),
          // width: "100px",
          required: true
        },
        adg: {
          prop: "adg",
          label: this.$t("Comn_adg_g"),
          // width: "100px",
          required: true
        },
        biomass: {
          prop: "biomass",
          label: `${this.$t("Comn_biomass")}(${this.$t("Comn_kg")})`,
          required: true
        },
        survival_percnt: {
          prop: "survival_percentage",
          label: this.$t("Comn_survival_percentage"),
          required: true
        },
        fcr: {
          prop: "fcr",
          label: this.$t("Comn_fcr"),
          required: true
        },
        event_date: {
          prop: "date",
          label: this.$t("Medicines_event_date"),
          required: true
        },
        samples_weight: {
          label: this.$t("ABW_samples_weight"),
          required: false
        },
        samples: {
          label: this.$t("Comn_samples"),
          required: false
        },
        actions: {
          label: this.$t("Comn_actions"),
          required: true,
          minWidth: 100
        }
      };
    },
    calculateBiomass: function() {
      return abwRec => {
        const pl_stocked = (
          this.getPondIdPondMap[this.getSelectedPondId] || {
            post_larva_stocked: 0
          }
        ).post_larva_stocked;
        const sur_percnt = abwRec.survival_percentage * 0.01;
        const abw = abwRec.abw * 0.001;
        return pl_stocked * sur_percnt * abw;
      };
    },
    selectedPondDetails: function() {
      return this.getPondIdPondMap[this.getSelectedPondId];
    }
  },
  async mounted() {
    await this.initComponent();
  },
  methods: {
    ...mapActions("pondDetails", {
      fetchPondABWValues: "fetchPondABWValues",
      deleteABWRecord: "deleteABWRecord"
    }),
    handleChangeInABW(changedABWValue, abwRecord) {
      abwRecord.modified = true;
      if (!isFinite(+changedABWValue)) return;
      abwRecord.awg = this.calcWeightGain(changedABWValue, abwRecord.last_abw);
      const agg = abwRecord.samples.reduce(
        (acc, x) => {
          if (!isFinite(+x.no_of_shrimps) || !isFinite(x.weight)) return acc;
          acc[0] += x.no_of_shrimps;
          acc[1] += x.weight;
          return acc;
        },
        [0, 0]
      );
      if (agg[0] === 0 && agg[1] === 0) return;
      abwRecord.samples.forEach(sample => {
        sample.abw = changedABWValue;
        sample.weight = changedABWValue * sample.no_of_shrimps;
      });
      abwRecord.sample_total_weight = abwRecord.samples.reduce(
        (acc, curr) => acc + curr.weight,
        0
      );
    },
    handleDialogClose() {
      this.resetForm();
    },
    async initComponent() {
      this.loading = true;
      try {
        this.dateRangeSelected = [
          this.$moment
            .utc(this.getCurrentCulture.start_date)
            .format("YYYY-MM-DD"),
          this.$moment.utc(this.getCurrentCulture.end_date).format("YYYY-MM-DD")
        ];
        await this.fetchPondABWValues({
          from_date: this.dateRangeSelected[0],
          to_date: this.dateRangeSelected[1]
        });
        this.tableData = this.$lodash
          .cloneDeep(this.getArrSelectedPondABWRecords)
          .map(x => {
            x.day_of_culture =
              this.$moment
                .utc(x.date)
                .diff(
                  this.$moment
                    .utc(`${this.dateRangeSelected[0]}T00:00:00.000Z`)
                    .startOf("days"),
                  "days"
                ) + 1;
            return x;
          })
          .sort((a, b) => b.day_of_culture - a.day_of_culture);
        this.tableData = this.tableData.map((rec, index) => {
          rec.isPopUpVisible = false;
          rec.index = index;
          rec.sample_total_weight = rec.samples.reduce((acc, curr) => {
            if (!acc) {
              acc = curr.weight;
            } else {
              acc += curr.weight;
            }
            return acc;
          }, undefined);
          rec.adg = rec.abw / rec.day_of_culture;
          rec.awg = rec.abw - rec.last_abw;
          if (index === this.tableData.length - 1) {
            rec.awg = rec.abw;
          }
          return rec;
        });
      } catch (err) {
        this.errorMessages(err, true);
      } finally {
        this.loading = false;
      }
    },
    handleEditABWRecord(abwRec) {
      try {
        this.isEditable(this.tabData);
        this.showDialog = true;
        this.editABWObj = JSON.parse(JSON.stringify(abwRec));
        if (this.editABWObj.samples.length === 0) {
          this.editABWObj.samples = [new Samples()];
        }
      } catch (error) {
        this.errorMessages(error, true);
      }
    },
    calcWeightGain: function(latest_abw, last_abw) {
      let weight_gain = last_abw;
      weight_gain = Number((latest_abw - (last_abw || 0)).toFixed(2));
      return weight_gain;
    },
    async handleDeleteABWRecord(abwRec) {
      const pond_name = this.selectedPondDetails.title;
      try {
        this.canUserDelete(this.tabData);
        await this.deleteConfirmation(this.$t("ABW_abw"), pond_name);
        await this.deleteABWRecord(abwRec._id);
        await this.initComponent();
        this.notifySuccess({
          msgLangkey: {
            title: "Comn_delete_success_msg",
            count: 0,
            child: {
              item: this.$t("ABW_abw"),
              source: pond_name
            }
          }
        });
      } catch (err) {
        if (err !== "cancel") {
          this.errorMessages(err, true);
        }
      }
    },
    handleCancelClick() {
      this.showDialog = false;
      this.editABWObj.isPopUpVisible = false;
    },

    async handleSaveEdits() {
      try {
        this.loading = true;
        const editObj = this.editABWObj;
        const aggSamples = editObj.samples.reduce(
          (acc, sample) => {
            if (!isFinite(+sample.no_of_shrimps) || !isFinite(sample.weight)) {
              return acc;
            }
            acc[0] += sample.weight;
            acc[1] += sample.no_of_shrimps;
            return acc;
          },
          [0, 0]
        );
        let { samples, ...payload } = editObj;
        if (aggSamples[0] > 0 && aggSamples[1] > 0) {
          samples = samples.map(sample => {
            const abw = editObj.abw;
            const weight = abw * sample.no_of_shrimps;
            return {
              ...sample,
              abw: abw,
              weight
            };
          });
          payload.samples = samples;
        }
        await this.$store.dispatch("pond/saveABWValues", [payload]);
        this.notifySuccess({
          msgLangkey: {
            title: "Comn_update_success_msg",
            count: 1,
            child: {
              item: this.$t("ABW_abw"),
              source: this.getSelectedPondName
            }
          }
        });
        this.showDialog = false;
        await this.initComponent();
      } catch (error) {
        this.errorMessages(error, true);
      } finally {
        this.loading = false;
        this.editABWObj.isPopUpVisible = false;
      }
    },
    handleOpenPopover(abwObj) {
      if (!abwObj.isPopUpVisible) {
        this.cancelObjCopy = this.$lodash.cloneDeep(abwObj);
        this.$set(abwObj, "isPopUpVisible", true);
      } else {
        this.handleClosePopper(abwObj);
      }
    },
    handleClosePopper: function(abwObj) {
      abwObj.isPopUpVisible = false;
      // abwObj.modified = true;
      // this.location_name = "";
    },
    handleSampleTableCancel(abwObj) {
      this.editABWObj = abwObj;
    }
  }
};
</script>

<style lang="scss">
@import "../../styles/mixins.scss";
.pond-abw-tab-calculator {
  @include respond-to(medium-screen) {
    top: 137px !important;
  }
  @include respond-to(tablet) {
    top: 137px !important;
  }
}
.pond-abw-tab {
  .el-input__inner {
    text-align: center;
  }
  .el-select,
  .el-input-number {
    width: 100%;
  }
}
.pondABWTab_Dialog {
  .el-form-item--small .el-form-item__content,
  .el-form-item--small .el-form-item__label {
    @include responsiveProperty(
      font-size,
      $app_font_size_1,
      $app_font_size_2,
      $app_font_size_3
    );
    @include responsiveProperty(width, 210px, 300px, 350px);
  }
}
</style>
